// Per-game thumbnail renderer. A template is a background plate + layers (see
// migration 0042 for the layer shape). Everything is painted on a <canvas> at
// the template's native size (1920x1080) and exported as a JPEG blob that the
// Schedule dialog uploads to Cloudflare Images.
//
// Text layers run through the same <token> engine as titles; image layers pull
// the away / home / league logo. Cross-origin images are loaded with
// crossOrigin='anonymous' (imagedelivery.net sends ACAO:*), which keeps the
// canvas exportable.
import { renderTemplate } from './templates'

const imgCache = new Map()

export function loadImage(url) {
  if (!url) return Promise.resolve(null)
  if (imgCache.has(url)) return imgCache.get(url)
  const p = new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)   // a missing logo shouldn't sink the whole card
    img.src = url
  })
  imgCache.set(url, p)
  return p
}

// Resolve a relative background_url (our bundled plates live under /thumb-templates/).
export const absUrl = (u) => (/^https?:\/\//i.test(u || '') ? u : new URL(u || '', window.location.origin).toString())

const fontSpec = (l) => `${l.italic ? 'italic ' : ''}${l.weight || 700} ${l.size || 40}px "${l.font || 'Open Sans'}", system-ui, sans-serif`

export async function ensureFonts(layers) {
  if (!document.fonts?.load) return
  const specs = new Set(layers.filter(l => l.type === 'text').map(l => fontSpec(l)))
  await Promise.all([...specs].map(s => document.fonts.load(s).catch(() => {})))
}

// Resolve the value of a layer for one game.
//   ctx     token map (gameContext)
//   sources { 'away-logo': url, 'home-logo': url, 'league-logo': url, 'away-color': '#…', 'home-color': '#…' }
export function layerImageUrl(layer, sources) {
  const s = layer.source || ''
  if (s === 'away-logo' || s === 'home-logo' || s === 'league-logo') return sources[s] || ''
  return s
}
function layerColor(layer, sources) {
  const c = layer.color || '#ffffff'
  if (c === 'away-color' || c === 'home-color') return sources[c] || layer.fallbackColor || '#111111'
  return c
}

// Draw text within w; shrink the font down to 50% before clipping.
function drawText(g, layer, text, color) {
  let size = layer.size || 40
  const minSize = Math.max(8, Math.round(size * 0.5))
  g.textBaseline = 'alphabetic'
  g.fillStyle = color
  const align = layer.align || 'left'
  g.textAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left'
  const w = layer.w || 0
  for (;;) {
    g.font = fontSpec({ ...layer, size })
    if (!w || g.measureText(text).width <= w || size <= minSize) break
    size -= 2
  }
  const x = align === 'center' ? layer.x + w / 2 : align === 'right' ? layer.x + w : layer.x
  if (layer.shadow) {
    g.save()
    g.shadowColor = layer.shadow.color || 'rgba(0,0,0,0.6)'
    g.shadowBlur = layer.shadow.blur ?? 8
    g.shadowOffsetX = layer.shadow.x ?? 0
    g.shadowOffsetY = layer.shadow.y ?? 2
    g.fillText(text, x, layer.y)
    g.restore()
  } else {
    g.fillText(text, x, layer.y)
  }
  if (layer.stroke) {
    g.lineWidth = layer.stroke.width || 2
    g.strokeStyle = layer.stroke.color || '#000'
    g.lineJoin = 'round'
    g.strokeText(text, x, layer.y)
  }
}

// Contain-fit an image inside the layer box, centred.
function drawImage(g, layer, img) {
  if (!img) return
  const { x, y, w, h } = layer
  const r = Math.min(w / img.naturalWidth, h / img.naturalHeight)
  const dw = img.naturalWidth * r, dh = img.naturalHeight * r
  g.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh)
}

// Render one game onto a canvas. Returns the canvas (caller exports or previews).
export async function renderThumb(template, { ctx, sources }, { canvas = null, scale = 1 } = {}) {
  const W = template.width || 1920, H = template.height || 1080
  const layers = Array.isArray(template.layers) ? template.layers : []
  const c = canvas || document.createElement('canvas')
  c.width = Math.round(W * scale); c.height = Math.round(H * scale)
  const g = c.getContext('2d')
  g.setTransform(scale, 0, 0, scale, 0, 0)

  const [bg] = await Promise.all([loadImage(absUrl(template.background_url)), ensureFonts(layers)])
  if (bg) g.drawImage(bg, 0, 0, W, H)
  else { g.fillStyle = '#222'; g.fillRect(0, 0, W, H) }

  const images = await Promise.all(layers.map(l => (l.type === 'image' ? loadImage(layerImageUrl(l, sources)) : null)))
  layers.forEach((l, i) => {
    if (l.hidden) return
    if (l.type === 'image') drawImage(g, l, images[i])
    else if (l.type === 'text') {
      let text = renderTemplate(l.text || '', ctx)
      if (l.uppercase) text = text.toUpperCase()
      if (text) drawText(g, l, text, layerColor(l, sources))
    }
  })
  return c
}

export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.92) {
  return new Promise((resolve, reject) => canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Canvas export failed (tainted by a cross-origin image?)'))), type, quality))
}

// Upload a rendered blob through vader's CF Images passthrough; returns the /public URL.
export async function uploadThumb(apiRaw, blob, filename) {
  const form = new FormData()
  form.append('file', blob, filename)
  const res = await apiRaw('/images/upload', { method: 'POST', body: form })
  const r = res?.result
  if (!res?.success || !r) throw new Error(res?.errors?.[0]?.message || 'Image upload failed')
  const pub = (r.variants || []).find(v => /\/public$/.test(v)) || r.variants?.[0]
  if (!pub) throw new Error('Upload succeeded but no variant URL was returned')
  return { url: pub, id: r.id }
}

// Filename that reads well in Image Manager: pihl-2026-10-01-morgantown-at-deer-lakes.jpg
export function thumbFilename(season, g) {
  const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  return `${slug(season?.league)}-${g.game_date}-${slug(g.visitor)}-at-${slug(g.home)}.jpg`
}
